<template>
  <div class="auth-main v1">
    <div class="auth-wrapper">
      <div class="auth-form">
        <div class="card my-5">
          <div class="card-body">
            <div class="text-center">
              <img src="/src/assets/images/logo/vesproc120.png" alt="VESPROC" class="img-fluid mb-3" />
              <h4 class="f-w-500 mb-4">{{ $t('auth.login.title') }}</h4>
            </div>
            

            
            <!-- Alert messages -->
            <div v-if="error" class="alert alert-danger alert-dismissible fade show" role="alert">
              {{ error }}
              <button type="button" class="btn-close" @click="error = null"></button>
            </div>
            
            <!-- 2FA Required Message -->
            <div v-if="requires2FA" class="alert alert-warning alert-dismissible fade show" role="alert">
              <h6 class="alert-heading">{{ $t('auth.login.twoFactorRequired') }}</h6>
              <p class="mb-0">{{ $t('auth.login.twoFactorMessage') }}</p>
              <button type="button" class="btn-close" @click="requires2FA = false"></button>
            </div>
            
            <!-- Email verification required message -->
            <div v-if="requiresEmailVerification" class="alert alert-warning alert-dismissible fade show" role="alert">
              <h6 class="alert-heading">{{ $t('auth.login.emailVerificationRequired') }}</h6>
              <p class="mb-2">{{ $t('auth.login.emailVerificationMessage') }}</p>
              <hr>
              <div class="d-flex justify-content-between align-items-center">
                <span>{{ $t('auth.login.didntReceiveEmail') }}</span>
                <button 
                  type="button" 
                  class="btn btn-outline-primary btn-sm" 
                  @click="resendVerification"
                  :disabled="isResending"
                >
                  <span v-if="isResending" class="spinner-border spinner-border-sm me-1"></span>
                  {{ $t('auth.login.resendVerification') }}
                </button>
              </div>
              <button type="button" class="btn-close" @click="requiresEmailVerification = false"></button>
            </div>
            
            <form @submit.prevent="login">
            <div class="mb-3">
              <input
                type="email"
                class="form-control"
                id="floatingInput"
                :placeholder="$t('auth.login.email')"
                v-model="email"
                required
              />
              <div v-if="v$.email.$error" class="text-danger text-start">
                {{ v$.email.$errors[0].$message }}
              </div>
            </div>
            
            <div class="mb-3">
              <div class="position-relative">
                <input
                  :type="showPassword ? 'text' : 'password'"
                  class="form-control pe-5"
                  id="floatingInput1"
                  :placeholder="$t('auth.login.password')"
                  v-model="password"
                  required
                />
                <button 
                  type="button" 
                  class="btn btn-link position-absolute end-0 top-50 translate-middle-y pe-3 text-muted"
                  @click="showPassword = !showPassword"
                  style="z-index: 10; border: none; background: none;"
                >
                  <i :class="showPassword ? 'ph-duotone ph-eye-slash' : 'ph-duotone ph-eye'"></i>
                </button>
              </div>
              <div v-if="v$.password.$error" class="text-danger text-start">
                {{ v$.password.$errors[0].$message }}
              </div>
            </div>
            
            <!-- 2FA Code Input (shown for super admin with 2FA) -->
            <div v-if="show2FAInput" class="mb-3">
              <input
                type="text"
                class="form-control text-center"
                :placeholder="$t('auth.login.twoFactorCodePlaceholder')"
                v-model="twoFactorCode"
                maxlength="6"
                pattern="[0-9]{6}"
              />
              <div class="form-text text-center">{{ $t('auth.login.twoFactorCode') }}</div>
              <div v-if="v$.twoFactorCode.$error" class="text-danger text-start">
                {{ v$.twoFactorCode.$errors[0].$message }}
              </div>
            </div>
            
            <div class="d-flex mt-1 justify-content-between align-items-center">
              <div class="form-check">
                <input class="form-check-input input-primary" type="checkbox" id="customCheckc1" v-model="rememberMe" checked />
                <label class="form-check-label text-muted" for="customCheckc1">
                  {{ $t('auth.login.remember') }}
                </label>
              </div>
              <router-link to="/forgot-password">
                <h6 class="f-w-400 mb-0">{{ $t('auth.login.forgotPassword') }}</h6>
              </router-link>
            </div>
            
            <div class="d-grid mt-4">
                <button type="submit" class="btn btn-primary" :disabled="isLoading">
                <span v-if="isLoading" class="spinner-border spinner-border-sm me-1"></span>
                {{ show2FAInput ? $t('auth.login.loginWith2FA') : $t('auth.login.loginButton') }}
              </button>
            </div>
            </form>

            <!--
              Sign-up was reachable only by typing /register: the link had been commented
              out here, so a supplier arriving at the product had no way in.

              Below the button rather than above the form, so it does not compete with the
              task most visitors came for, and hidden during the two-factor step, where the
              account already exists and the only thing that matters is the code.
            -->
            <p v-if="!show2FAInput" class="text-center mt-3 mb-0">
              {{ $t('auth.login.noAccount') }}
              <router-link to="/register" class="link-primary ms-1">{{ $t('auth.login.register') }}</router-link>
            </p>
            

          </div>
        </div>
      </div>
      <AuthFooter />
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import { useOnboardingGate } from '@/composables/useOnboardingGate';
import { useI18n } from 'vue-i18n';
import useVuelidate from '@vuelidate/core';
import { required, email as emailValidator, minLength, maxLength } from '@vuelidate/validators';
import authService from '@/services/authService';
import { syncServerLanguage } from '@/i18n';
import AuthFooter from '@/components/auth/AuthFooter.vue';

export default {
  components: {
    AuthFooter
  },
  name: 'LoginPage',
  setup() {
    const store = useStore();
    const router = useRouter();
    const route = useRoute();
    const { t, locale } = useI18n();
    
    const email = ref('');
    const password = ref('');
    const showPassword = ref(false);
    const twoFactorCode = ref('');
    const rememberMe = ref(false);
    const error = ref(null);
    const isLoading = ref(false);
    const requiresEmailVerification = ref(false);
    const requires2FA = ref(false);
    const show2FAInput = ref(false);
    const isResending = ref(false);

    const isSuperAdmin = computed(() => {
      return authService.isSuperAdmin(email.value);
    });

    // Validation rules
    const rules = computed(() => {
      const baseRules = {
        email: { 
          required, 
          email: emailValidator 
        },
        password: { 
          required 
        }
      };

      if (show2FAInput.value) {
        baseRules.twoFactorCode = {
          required,
          minLength: minLength(6),
          maxLength: maxLength(6)
        };
      }

      return baseRules;
    });

    const v$ = useVuelidate(rules, { 
      email, password, twoFactorCode 
    });



    const login = async () => {
      error.value = null;
      requiresEmailVerification.value = false;
      requires2FA.value = false;
      
      // Validate form
      const isFormValid = await v$.value.$validate();
      if (!isFormValid) return;
      
      isLoading.value = true;
      
      try {
        let result;
        
        // Determine login method based on user type and 2FA input
        if (show2FAInput.value && twoFactorCode.value) {
          // 2FA kodu girilmişse login-with-two-factor endpoint'ini kullan
          result = await store.dispatch('auth/loginWith2FA', {
            email: email.value,
            password: password.value,
            twoFactorCode: twoFactorCode.value,
            remember: rememberMe.value
          });
        } else {
          // Standard login or super admin without 2FA
          result = await store.dispatch('auth/login', {
            email: email.value,
            password: password.value,
            remember: rememberMe.value
          });
        }
        
        // If we get here, login was successful
        syncServerLanguage(locale.value, { force: true });
        store.dispatch('notify', {
          message: t('common.notification.loginSuccess'),
          type: 'success'
        });
        
        // Check if there's a redirect query parameter
        const redirectPath = route.query.redirect;
        
        if (redirectPath && typeof redirectPath === 'string') {
          // Redirect to the original URL
          router.push(redirectPath);
        } else if (result.user?.isSuperAdmin) {
          router.push('/admin');
        } else {
          // A supplier who has not finished registering lands on the registration flow,
          // not the profile. Without this the steps exist but nothing points at them.
          const { resolveLandingRoute } = useOnboardingGate();
          router.push(await resolveLandingRoute('/profile'));
        }
      } catch (err) {
        console.error('Login error:', err);
        console.error('Error response:', err.response);
        console.error('Error response data:', err.response?.data);
        console.error('Error message path:', err.response?.data?.error?.message);
        
        const errorMessage = err.response?.data?.error?.message || err.response?.data?.message;
        
        // Handle specific error cases
        if (errorMessage === 'RequiresConfirmUser') {
          
          requiresEmailVerification.value = true;
          store.commit('auth/setUnverifiedEmail', email.value);
        } else if (errorMessage?.includes('Two-factor authentication is required')) {
          requires2FA.value = true;
          show2FAInput.value = true;
        } else if (errorMessage?.includes('Invalid two-factor authentication code')) {
          error.value = t('common.notification.invalidTwoFactorCode');
        } else {
          error.value = errorMessage || t('common.notification.loginError');
        }
      } finally {
        isLoading.value = false;
      }
    };

    const resendVerification = async () => {
      isResending.value = true;
      
      try {
        await store.dispatch('auth/resendVerificationEmail');
        
        // Success - show notification and close verification alert
        store.dispatch('notify', {
          message: t('common.notification.resendVerificationSuccess'),
          type: 'success'
        });
        
        // Close the verification alert
        requiresEmailVerification.value = false;
        
      } catch (err) {
        console.error('Resend verification error:', err);
        
        // Show specific error message from API
        const errorMessage = err.response?.data?.error?.message || 
                           err.response?.data?.message || 
                           t('common.notification.resendVerificationError');
        
        error.value = errorMessage;
        
        // Also show as notification
        store.dispatch('notify', {
          message: errorMessage,
          type: 'error'
        });
      } finally {
        isResending.value = false;
      }
    };

    return {
      email,
      password,
      twoFactorCode,
      rememberMe,
      error,
      isLoading,
      requiresEmailVerification,
      requires2FA,
      show2FAInput,
      isResending,
      showPassword,
      isSuperAdmin,
      login,
      resendVerification,
      v$
    };
  }
};
</script> 

<style scoped>
.auth-main.v1 {
  background-color: #f2f2f2;
  background-image: url('/src/assets/images/logo/worldmap.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

</style>